<template>
    <div :data-id="alignmentId" class="alignement-vignette-container border rounded mb-2 pl-8 pr-8">
        <!-- Line 1: Informations principales -->
        <div class="vignette-line">
            <!-- Bloc 1: Identifiant et alignements -->
            <div class="vignette-bloc vignette-bloc-1">
                <p class="alignement-title font-weight-bold primary--text">
                    {{ alignmentId }}
                </p>
            </div>

            <!-- Bloc 2: Version et Date -->
            <div class="vignette-bloc vignette-bloc-2">
                <p class="ma-0 ans-mb">
                    Version :
                    <span class="font-weight-bold black--text">{{ versionLabel }}</span>
                </p>
                <p class="ma-0 ans-mb">
                    Date de publication :
                    <span class="font-weight-bold black--text">{{ publicationDate }}</span>
                </p>
            </div>

            <!-- Bloc 3: Visibilité -->
            <div class="vignette-bloc vignette-bloc-3">
                <p class="ma-0 ans-mb">
                    Alignement :
                    <a :href="sourceHref" class="font-weight-bold primary--text alignement-link">{{ sourceTitle
                    }}</a>
                    vers
                    <a :href="targetHref" class="font-weight-bold primary--text alignement-link">
                        {{ targetTitle }}
                    </a>
                </p>
            </div>
        </div>

        <div class="alignement-links">


            <!-- Bouton de téléchargement du zip pour alignements protégés -->
            <a v-if="isConnectionNeeded" type="button"
                class="terminologie-links-btn terminologie-zip-btn btn btn--style1 btn--white border mr-1 file-link"
                data-toggle="modal" data-target="#popin-login-required-zip">
                <i class="fas fa-lock fa-1x pr-1"></i>Télécharger zip
            </a>
            <!-- Bouton de téléchargement du zip pour alignements non protégés -->
            <a v-else type="button"
                class="terminology-load-licence terminologie-links-btn terminologie-zip-btn btn btn--style1 btn--white border mr-1 file-link"
                :data-terminology-licence="licenceLabel" :data-terminology-id="alignmentId"
                :data-terminology-version="versionLabel" data-is-alignment="1">
                <i style="font-style: normal;">Télécharger zip</i>
            </a>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AlignementVignette',
    props: {
        alignment: {
            type: Object,
            required: true
        }
    },
    computed: {
        /**
         * Retourne l'identifiant d'alignement a afficher.
         * @returns {string}
         */
        alignmentId() {
            return this.alignment?.alignementId || '';
        },
        /**
         * Retourne le libelle de licence de l'alignement.
         * @returns {string}
         */
        licenceLabel() {
            return this.alignment?.licenceLabel || '';
        },
        /**
         * Retourne la version de l'alignement.
         * @returns {string}
         */
        versionLabel() {
            return this.alignment?.versionInfo || '-';
        },
        /**
         * Calcule la date de publication au format JJ/MM/YYYY.
         * @returns {string}
         */
        publicationDate() {
            const date = this.alignment?.publishedDate || this.alignment?.dateImportSMT || '';
            if (!date) return '';
            return this.formatDate(date);
        },
        /**
         * Retourne l'objet de terminologie source.
         * @returns {Object}
         */
        terminologySource() {
            return this.alignment?.terminologySource || {};
        },
        /**
         * Retourne l'objet de terminologie cible.
         * @returns {Object}
         */
        terminologyTarget() {
            return this.alignment?.terminologyTarget || {};
        },
        /**
         * Retourne le libelle de la terminologie source.
         * @returns {string}
         */
        sourceTitle() {
            return this.alignment?.terminologySourceLabel || 'Source inconnue';
        },
        /**
         * Retourne le libelle de la terminologie cible.
         * @returns {string}
         */
        targetTitle() {
            return this.alignment?.terminologyTargetLabel || 'Cible inconnue';
        },

        /**
         * Retourne l'identifiant de la terminologie source.
         * @returns {string}
         */
        terminologySourceId() {
            return this.alignment?.terminologySourceId || '';
        },
        sourceHref() {
            return `${this.$searchResultManager.wordPressBaseUrl}/${this.terminologySourceId}/`;
        },
        /**
         * Retourne la version de la terminologie source.
         * @returns {string}
         */
        terminologySourceVersion() {
            return this.alignment?.terminologySourceVersion || '';
        },
        /**
         * Retourne l'identifiant de la terminologie cible.
         * @returns {string}
         */
        terminologyTargetId() {
            return this.alignment?.terminologyTargetId || '';
        },
        targetHref() {
            return `${this.$searchResultManager.wordPressBaseUrl}/${this.terminologyTargetId}/`;
        },
        /**
         * Retourne la version de la terminologie cible.
         * @returns {string}
         */
        terminologyTargetVersion() {
            return this.alignment?.terminologyTargetVersion || '';
        },
        /**
         * Determine si la connexion est requise pour le telechargement.
         * @returns {boolean}
         */
        isConnectionNeeded() {
            const isProtected = !!this.alignment?.protected;
            const isUserLoggedIn = !!this.$searchResultManager?.isUserLoggedIn;
            return isProtected && !isUserLoggedIn;
        }
    },
    methods: {
        /**
         * Formate une date au format JJ/MM/YYYY
         * @param {string|Date} date - La date à formater
         * @returns {string} La date formatée
         */
        formatDate(date) {
            if (!date) return '';
            try {
                const dateObj = typeof date === 'string' ? new Date(date) : date;
                if (Number.isNaN(dateObj)) return date;
                const day = String(dateObj.getDate()).padStart(2, '0');
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const year = dateObj.getFullYear();
                return `${day}/${month}/${year}`;
            } catch {
                return date;
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.alignement-title {
    margin-bottom: 7.5px;
}

.alignement-link {
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
}

.alignement-vignette-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    overflow: hidden;
    background-color: #fff;
    margin-bottom: 1rem;
    width: 80%;
    margin-left: auto;
    margin-right: auto;
    border-radius: 1.4rem !important;

    &:hover {
        border-color: var(--v-primary-base);
    }
}

.vignette-line {
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    border-radius: 0.5rem;
    overflow: hidden;
    background-color: #fff;
    margin-top: 1rem;
    margin-bottom: 1rem;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
}

.vignette-bloc {
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    height: 100%;
}

.vignette-bloc-1 {
    flex: 1;
}

.vignette-bloc-2,
.vignette-bloc-3 {
    flex: 1;
}

.ans-mb {
    margin-bottom: 0.75rem !important;
}

.alignement-links {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0 0 1rem;
}

@media (max-width: 1300px) {
    .vignette-line {
        flex-direction: column;
        width: 90%;
    }

    .alignement-vignette-container {
        flex-direction: column;
        width: 90%;
    }

    .vignette-bloc {
        &:not(:last-child)::after {
            display: none;
        }

        &:not(:last-child) {
            border-bottom: 1px solid #dadada;
            padding-bottom: 1rem;
        }

        &:not(:first-child) {
            padding-top: 1rem;
        }
    }

    .ans-mb {
        margin-bottom: 0.5rem !important;
    }
}

@media (max-width: 400px) {
    .vignette-line {
        flex-direction: column;
        width: 100%;
    }

    .alignement-vignette-container {
        flex-direction: column;
        width: 100%;
    }
}
</style>
